var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { reaction, observable, computed } from "mobx";
import { Persistence, SocialLoginAuthState } from "../Infra/Persistence";
import { NamedRoutes } from "./NamedRoutes";
import { LoginAnalyticsEvent } from "healthex-common/analytics.dtos";
import { getHeapGlobalEventProperties } from "../Infra/LoadHeap";
import { generateOAuthState, getAnonymousUserId, LoginFlow } from "../Logic/AuthManager";
import { GFIDProvider, isSocialLoginGfidProvider } from "healthex-common/healthcareorganization.dtos";
import { asEnum, willNeverHappen } from "healthex-common/utils";
import { isOk } from "healthex-common/result";
import { effectiveQueryParams } from "./queryParams";
import { getObservableT } from "../Infra/i18next";
const t = getObservableT("patients");
export const HIGHER_AUTH_TIER_REQUIRED = "HIGHER_AUTH_TIER_REQUIRED";
export function analyticsErrorCode(error) {
    return error === HIGHER_AUTH_TIER_REQUIRED ? "CLEAR_ACCOUNT_LINKING_REQUIRED" : error;
}
function parseRequiredProviders(raw) {
    if (!raw)
        return undefined;
    const parsed = raw.split(",").flatMap((value) => {
        const provider = asEnum(GFIDProvider)(value.trim());
        return provider ? [provider] : [];
    });
    return parsed.length > 0 ? parsed : undefined;
}
export class SocialLoginAuthManager {
    constructor(authManager, persistence, routingUIManager, analyticsService, environment, verificationService, bannerUIManager) {
        this.authManager = authManager;
        this.persistence = persistence;
        this.routingUIManager = routingUIManager;
        this.analyticsService = analyticsService;
        this.environment = environment;
        this.verificationService = verificationService;
        this.bannerUIManager = bannerUIManager;
        this.isCompletingFlow = false;
        this.state = SocialLoginAuthState.INACTIVE;
        this._errorMessage = null;
        this.requestedGFIDProvider = null;
        this.responseGFIDProvider = null;
        this.pendingSocialLoginData = null;
        this.pendingSocialLoginData = this.persistence.getPendingSocialLoginData();
        reaction(() => this.routingUIManager.currentRoute, (currentRoute) => {
            if ((currentRoute === null || currentRoute === void 0 ? void 0 : currentRoute.routeName) === NamedRoutes.SOCIAL_LOGIN_CALLBACK.routeName) {
                void this.processSocialLoginCallback();
            }
        }, { fireImmediately: true, name: "SocialLoginAuthManager_currentRoute" });
        reaction(() => this.authManager.isLoggedIn, (isLoggedIn) => {
            if (isLoggedIn && !this.isCompletingFlow) {
                this.resetAuth();
            }
        }, { name: "SocialLoginAuthManager_isLoggedIn" });
        window.addEventListener("pageshow", (event) => {
            if (event.persisted) {
                this.resetAuthStateIfNeeded();
            }
        });
    }
    get errorMessage() {
        return this.state === SocialLoginAuthState.ERROR ? this._errorMessage : null;
    }
    get isAccountLinkingError() {
        return this.state === SocialLoginAuthState.ERROR && this._errorMessage === HIGHER_AUTH_TIER_REQUIRED;
    }
    get hasPendingSocialLogin() {
        return !!this.pendingSocialLoginData;
    }
    get pendingPhoneVerified() {
        if (this.pendingSocialLoginData) {
            return !!this.pendingSocialLoginData.phoneVerified;
        }
        return null;
    }
    get shouldShowAccountSetupScreen() {
        const pending = this.pendingSocialLoginData;
        if (!pending)
            return null;
        return pending.showAccountSetup && this.authManager.linkingStatus === null;
    }
    get hasCompletedAuth() {
        return this.state === SocialLoginAuthState.SUCCESS || this.state === SocialLoginAuthState.ERROR;
    }
    async redirectToSocialLoginPage(callbackHash, gfidProvider, source, organizationId) {
        var _a, _b;
        this.state = SocialLoginAuthState.IN_PROGRESS;
        this.requestedGFIDProvider = gfidProvider;
        const heapGlobalEventProperties = { ...getHeapGlobalEventProperties(), source, provider: gfidProvider };
        const userId = (_a = getAnonymousUserId()) !== null && _a !== void 0 ? _a : undefined;
        const identity = (_b = this.authManager.id) !== null && _b !== void 0 ? _b : undefined;
        await this.analyticsService.trackHeapEvent({
            event: LoginAnalyticsEvent.LOGIN_ATTEMPTED,
            identity,
            user_id: userId,
            properties: { ...heapGlobalEventProperties },
        });
        const resolvedOrganizationId = typeof organizationId === "function" ? await organizationId() : organizationId;
        const url = this.getSocialLoginUrl(gfidProvider);
        if (!url) {
            this.handleAuthError();
            return;
        }
        const state = generateOAuthState();
        this.setSocialLoginAuthData({
            callbackHash,
            state,
            heapGlobalEventProperties,
            organizationId: resolvedOrganizationId !== null && resolvedOrganizationId !== void 0 ? resolvedOrganizationId : undefined,
        });
        window.location.href = `${url}?state=${state}`;
    }
    static checkAndReturnSocialLoginAuthResponse() {
        var _a;
        const socialLoginAuthData = new Persistence().getSocialLoginAuthData();
        if (!socialLoginAuthData)
            return null;
        const queryParams = effectiveQueryParams();
        const gfidProvider = asEnum(GFIDProvider)((_a = queryParams.get("social-login-provider")) !== null && _a !== void 0 ? _a : "");
        const state = queryParams.get("state");
        const token = queryParams.get("token");
        const error = queryParams.get("error");
        const linkingToken = queryParams.get("linkingToken");
        const requiredProviders = queryParams.get("requiredProviders");
        const isFirstTimeLogin = queryParams.get("isFirstTimeLogin") === "true";
        const verificationKey = queryParams.get("verificationKey");
        if (!!gfidProvider && !!state && isSocialLoginGfidProvider(gfidProvider) && (!!token || !!error || !!verificationKey)) {
            return {
                gfidProvider,
                token,
                error,
                linkingToken,
                requiredProviders,
                state,
                isFirstTimeLogin,
                verificationKey,
            };
        }
        else {
            return null;
        }
    }
    resetAuth() {
        this.state = SocialLoginAuthState.INACTIVE;
        this.requestedGFIDProvider = null;
        this.persistence.setSocialLoginAuthData(null);
        this.clearPendingSocialLogin();
    }
    clearPendingSocialLogin() {
        this.pendingSocialLoginData = null;
        this.persistence.setPendingSocialLoginData(null);
    }
    async completeVerificationFlow(demographics, onBeforeClear) {
        var _a, _b, _c, _d, _e, _f;
        const pendingData = this.pendingSocialLoginData;
        const stash = this.persistence.getConsentDeferredVerificationStash();
        const verificationKey = (_a = pendingData === null || pendingData === void 0 ? void 0 : pendingData.verificationKey) !== null && _a !== void 0 ? _a : stash === null || stash === void 0 ? void 0 : stash.verificationKey;
        const gfidProvider = (_b = pendingData === null || pendingData === void 0 ? void 0 : pendingData.gfidProvider) !== null && _b !== void 0 ? _b : stash === null || stash === void 0 ? void 0 : stash.gfidProvider;
        if (!verificationKey || !gfidProvider) {
            return { ok: false, code: "no_pending_data" };
        }
        const userId = (_c = getAnonymousUserId()) !== null && _c !== void 0 ? _c : undefined;
        const heapProperties = {
            ...getHeapGlobalEventProperties(),
            ...((_e = (_d = pendingData === null || pendingData === void 0 ? void 0 : pendingData.heapGlobalEventProperties) !== null && _d !== void 0 ? _d : stash === null || stash === void 0 ? void 0 : stash.heapGlobalEventProperties) !== null && _e !== void 0 ? _e : {}),
            provider: gfidProvider,
        };
        const result = await this.verificationService.completeVerification(verificationKey, demographics.firstName, demographics.lastName, demographics.dateOfBirth, demographics.phone, heapProperties);
        if (!result.ok) {
            void this.analyticsService.trackHeapEvent({
                event: LoginAnalyticsEvent.LOGIN_ERROR,
                user_id: userId,
                properties: heapProperties,
            });
            this.handleCompletionError();
            return { ok: false, code: result.code };
        }
        this.isCompletingFlow = true;
        try {
            await this.authManager.handleSocialLogin(result.token, this.getLoginFlow(gfidProvider), result.isFirstTimeLogin);
            await this.authManager.redeemPendingHostedSsoLink();
            await (onBeforeClear === null || onBeforeClear === void 0 ? void 0 : onBeforeClear());
            this.clearPendingSocialLogin();
            this.persistence.setConsentDeferredVerificationStash(null);
            this.persistence.setPendingSocialLoginSuccessMarker(null);
        }
        finally {
            this.isCompletingFlow = false;
        }
        void this.analyticsService.addUserProperties((_f = this.authManager.id) !== null && _f !== void 0 ? _f : undefined, {
            loginType: gfidProvider,
            loginCategory: "Social",
        });
        return { ok: true, isFirstTimeLogin: result.isFirstTimeLogin, gfidProvider };
    }
    handleCompletionError() {
        this.bannerUIManager.displayBanner({
            type: "ERROR",
            text: t("Something went wrong completing your login. Please try again."),
            durationInMS: 6000,
        });
        this.clearPendingSocialLogin();
        this.persistence.setConsentDeferredVerificationStash(null);
        this.persistence.setPendingSocialLoginSuccessMarker(null);
    }
    setSocialLoginAuthData(socialLoginAuthData) {
        this.persistence.setSocialLoginAuthData(socialLoginAuthData);
    }
    getSocialLoginAuthData() {
        return this.persistence.getSocialLoginAuthData();
    }
    async processSocialLoginCallback() {
        var _a, _b, _c;
        const socialLoginAuthData = this.getSocialLoginAuthData();
        if (!socialLoginAuthData) {
            this.handleAuthError();
            return;
        }
        const socialLoginAuthResponse = SocialLoginAuthManager.checkAndReturnSocialLoginAuthResponse();
        if (!socialLoginAuthResponse) {
            this.handleAuthError();
            return;
        }
        if (!socialLoginAuthData.state || !socialLoginAuthResponse.state || socialLoginAuthData.state !== socialLoginAuthResponse.state) {
            this.handleAuthError();
            return;
        }
        const { token, gfidProvider, error, linkingToken, requiredProviders, verificationKey } = socialLoginAuthResponse;
        this.responseGFIDProvider = gfidProvider;
        const userId = (_a = getAnonymousUserId()) !== null && _a !== void 0 ? _a : undefined;
        if (error) {
            this.handleAuthError(error, linkingToken, gfidProvider, requiredProviders);
            if (error === HIGHER_AUTH_TIER_REQUIRED) {
                this.trackSocialLoginComplete({
                    gfidProvider,
                    userId,
                    heapGlobalEventProperties: socialLoginAuthData.heapGlobalEventProperties,
                    extraProperties: { accountLinkingRequired: true },
                });
            }
            void this.analyticsService.trackHeapEvent({
                event: LoginAnalyticsEvent.LOGIN_ERROR,
                user_id: userId,
                properties: { ...socialLoginAuthData.heapGlobalEventProperties, error: analyticsErrorCode(error) },
            });
            window.history.replaceState(null, "", window.location.href.replace(window.location.search, ""));
            return;
        }
        if (verificationKey) {
            window.history.replaceState(null, "", window.location.href.replace(window.location.search, ""));
            const oauthState = socialLoginAuthData.state;
            if (!oauthState) {
                this.handleAuthError();
                return;
            }
            const fetchResult = await this.verificationService.fetchPendingData(verificationKey, oauthState);
            if (!fetchResult.ok) {
                this.handleAuthError();
                return;
            }
            const pendingData = {
                verificationKey,
                email: fetchResult.email,
                firstName: fetchResult.firstName,
                lastName: fetchResult.lastName,
                dateOfBirth: fetchResult.dateOfBirth,
                phoneNumber: fetchResult.phoneNumber,
                phoneVerified: fetchResult.phoneVerified,
                isExistingUser: fetchResult.isExistingUser,
                gfidProvider: fetchResult.gfidProvider,
                showAccountSetup: fetchResult.showAccountSetup,
                heapGlobalEventProperties: socialLoginAuthData.heapGlobalEventProperties,
                socialLoginSuccessTimestamp: new Date().toISOString(),
            };
            this.pendingSocialLoginData = pendingData;
            this.persistence.setPendingSocialLoginData(pendingData);
            this.trackSocialLoginComplete({
                gfidProvider,
                userId,
                heapGlobalEventProperties: socialLoginAuthData.heapGlobalEventProperties,
                extraProperties: {
                    isExistingUser: fetchResult.isExistingUser,
                    showAccountSetup: fetchResult.showAccountSetup,
                    phoneVerified: fetchResult.phoneVerified,
                },
            });
            this.handleAuthSuccess();
            return;
        }
        if (!token) {
            this.handleAuthError();
            window.history.replaceState(null, "", window.location.href.replace(window.location.search, ""));
            return;
        }
        try {
            const result = await this.authManager.handleSocialLogin(token, this.getLoginFlow(gfidProvider), socialLoginAuthResponse.isFirstTimeLogin);
            if (isOk(result)) {
                await this.authManager.redeemPendingHostedSsoLink();
                this.handleAuthSuccess();
                this.trackSocialLoginComplete({
                    gfidProvider,
                    userId,
                    identity: (_b = this.authManager.id) !== null && _b !== void 0 ? _b : undefined,
                    heapGlobalEventProperties: socialLoginAuthData.heapGlobalEventProperties,
                    extraProperties: { isFirstTimeLogin: socialLoginAuthResponse.isFirstTimeLogin },
                });
                void this.analyticsService.addUserProperties((_c = this.authManager.id) !== null && _c !== void 0 ? _c : undefined, {
                    loginType: gfidProvider,
                    loginCategory: "Social",
                });
            }
            else {
                this.handleAuthError();
                void this.analyticsService.trackHeapEvent({
                    event: LoginAnalyticsEvent.LOGIN_ERROR,
                    user_id: userId,
                    properties: { ...socialLoginAuthData.heapGlobalEventProperties },
                });
            }
        }
        catch (e) {
            console.error(`Error authenticating with Social Login ${gfidProvider}`, e);
            this.handleAuthError();
            void this.analyticsService.trackHeapEvent({
                event: LoginAnalyticsEvent.LOGIN_ERROR,
                user_id: userId,
                properties: { ...socialLoginAuthData.heapGlobalEventProperties },
            });
        }
        window.history.replaceState(null, "", window.location.href.replace(window.location.search, ""));
    }
    trackSocialLoginComplete(params) {
        void this.analyticsService.trackHeapEvent({
            event: LoginAnalyticsEvent.SOCIAL_LOGIN_COMPLETE,
            user_id: params.userId,
            identity: params.identity,
            properties: {
                ...params.heapGlobalEventProperties,
                provider: params.gfidProvider,
                ...params.extraProperties,
            },
        });
    }
    handleAuthError(error, linkingToken, gfidProvider, requiredProviders) {
        this.state = SocialLoginAuthState.ERROR;
        this._errorMessage = error !== null && error !== void 0 ? error : null;
        if (linkingToken) {
            this.setAccountLinkingData({ linkingToken, gfidProvider, requiredProviders: parseRequiredProviders(requiredProviders) });
        }
    }
    handleAuthSuccess() {
        this.state = SocialLoginAuthState.SUCCESS;
    }
    resetAuthStateIfNeeded() {
        if (this.persistence.getConsentDeferredVerificationStash()) {
            return;
        }
        const socialLoginAuthData = this.getSocialLoginAuthData();
        const pendingSocialLoginData = this.persistence.getPendingSocialLoginData();
        if (!!socialLoginAuthData || !!pendingSocialLoginData) {
            this.resetAuth();
        }
    }
    getSocialLoginUrl(gfidProvider) {
        if (gfidProvider === GFIDProvider.GOOGLE) {
            return `${this.environment.API_BASE_URL}/auth/google`;
        }
        else if (gfidProvider === GFIDProvider.MICROSOFT) {
            return `${this.environment.API_BASE_URL}/auth/microsoft`;
        }
        else if (gfidProvider === GFIDProvider.APPLE) {
            return `${this.environment.API_BASE_URL}/auth/apple`;
        }
        else if (gfidProvider === GFIDProvider.SMART_ON_FHIR ||
            gfidProvider === GFIDProvider.CLEAR ||
            gfidProvider === GFIDProvider.HX_TEST ||
            gfidProvider === GFIDProvider.HOSTED_SSO ||
            gfidProvider === GFIDProvider.DEVICE_TOKEN) {
            return null;
        }
        else {
            willNeverHappen(gfidProvider);
            return null;
        }
    }
    getLoginFlow(gfidProvider) {
        if (gfidProvider === GFIDProvider.GOOGLE) {
            return LoginFlow.GFID_GOOGLE;
        }
        else if (gfidProvider === GFIDProvider.MICROSOFT) {
            return LoginFlow.GFID_MICROSOFT;
        }
        else if (gfidProvider === GFIDProvider.APPLE) {
            return LoginFlow.GFID_APPLE;
        }
        else if (gfidProvider === GFIDProvider.HOSTED_SSO) {
            return LoginFlow.GFID_HOSTED_SSO;
        }
        else if (gfidProvider === GFIDProvider.SMART_ON_FHIR ||
            gfidProvider === GFIDProvider.CLEAR ||
            gfidProvider === GFIDProvider.HX_TEST ||
            gfidProvider === GFIDProvider.DEVICE_TOKEN) {
            return null;
        }
        else {
            willNeverHappen(gfidProvider);
            return null;
        }
    }
    enterAccountLinkingRequiredState(linkingToken, gfidProvider, requiredProviders) {
        this.state = SocialLoginAuthState.ERROR;
        this._errorMessage = HIGHER_AUTH_TIER_REQUIRED;
        this.setAccountLinkingData({ linkingToken, gfidProvider, requiredProviders });
    }
    setAccountLinkingData(accountLinkingData) {
        this.persistence.setAccountLinkingData(accountLinkingData);
    }
    getAccountLinkingData() {
        return this.persistence.getAccountLinkingData();
    }
}
__decorate([
    observable
], SocialLoginAuthManager.prototype, "state", void 0);
__decorate([
    observable
], SocialLoginAuthManager.prototype, "_errorMessage", void 0);
__decorate([
    computed
], SocialLoginAuthManager.prototype, "errorMessage", null);
__decorate([
    computed
], SocialLoginAuthManager.prototype, "isAccountLinkingError", null);
__decorate([
    observable
], SocialLoginAuthManager.prototype, "requestedGFIDProvider", void 0);
__decorate([
    observable
], SocialLoginAuthManager.prototype, "responseGFIDProvider", void 0);
__decorate([
    observable
], SocialLoginAuthManager.prototype, "pendingSocialLoginData", void 0);
__decorate([
    computed
], SocialLoginAuthManager.prototype, "hasPendingSocialLogin", null);
__decorate([
    computed
], SocialLoginAuthManager.prototype, "pendingPhoneVerified", null);
__decorate([
    computed
], SocialLoginAuthManager.prototype, "shouldShowAccountSetupScreen", null);
__decorate([
    computed
], SocialLoginAuthManager.prototype, "hasCompletedAuth", null);
